import React, { useState, useEffect } from 'react';
import { architectAPI, judgeAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function Dashboard() {
  const [stats, setStats] = useState({
    pendingQuestions: 0,
    approvedQuestions: 0,
    scoredArticles: 0,
    loading: true,
  });
  const [trends, setTrends] = useState({
    scoredArticles: '0%',
    pendingQuestions: '0%',
    approvedQuestions: '0%',
  });

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return '—';
    const change = ((current - previous) / previous) * 100;
    if (change > 0) return `+${change.toFixed(0)}%`;
    if (change < 0) return `${change.toFixed(0)}%`;
    return '0%';
  };

  const loadStats = async () => {
    try {
      const [pending, approved, articles] = await Promise.all([
        architectAPI.getPendingQuestions(),
        architectAPI.getApprovedQuestions(),
        judgeAPI.getScoredArticles(),
      ]);
      
      const newStats = {
        pendingQuestions: pending.data?.total || 0,
        approvedQuestions: approved.data?.total || 0,
        scoredArticles: articles.data?.total || 0,
        loading: false,
      };

      // Get previous stats from localStorage
      const prevStatsStr = localStorage.getItem('dashboardStats');
      const prevStats = prevStatsStr ? JSON.parse(prevStatsStr) : null;

      // Calculate trends
      if (prevStats) {
        setTrends({
          scoredArticles: calculateTrend(newStats.scoredArticles, prevStats.scoredArticles),
          pendingQuestions: calculateTrend(newStats.pendingQuestions, prevStats.pendingQuestions),
          approvedQuestions: calculateTrend(newStats.approvedQuestions, prevStats.approvedQuestions),
        });
      }

      // Save current stats for next comparison
      localStorage.setItem('dashboardStats', JSON.stringify(newStats));
      
      setStats(newStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load dashboard stats');
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const statCards = [
    {
      name: 'Scored Articles',
      value: stats.scoredArticles,
      gradient: 'from-blue-500 via-blue-600 to-indigo-700',
      icon: '📰',
      description: 'Articles meeting exam criteria',
      trend: trends.scoredArticles,
    },
    {
      name: 'Pending Questions',
      value: stats.pendingQuestions,
      gradient: 'from-amber-500 via-orange-600 to-red-600',
      icon: '⏳',
      description: 'Questions awaiting review',
      trend: trends.pendingQuestions,
    },
    {
      name: 'Approved Questions',
      value: stats.approvedQuestions,
      gradient: 'from-emerald-500 via-green-600 to-teal-700',
      icon: '✅',
      description: 'Production-ready questions',
      trend: trends.approvedQuestions,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-indigo-700 to-blue-800 p-8 shadow-2xl">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white mb-2">
            Welcome to Recent Affairs AI
          </h1>
          <p className="text-blue-100 text-lg">
            AI-powered content curation for CLAT & CUET preparation
          </p>
        </div>
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white opacity-10"></div>
        <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-white opacity-10"></div>
      </div>

      {stats.loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-blue-600"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-blue-100"></div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Grid with 3D Effect */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {statCards.map((stat, index) => (
              <div
                key={stat.name}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                }}
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-90`}></div>
                
                {/* Decorative Elements */}
                <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white opacity-10 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="absolute -left-8 -bottom-8 h-32 w-32 rounded-full bg-white opacity-10 group-hover:scale-150 transition-transform duration-500"></div>
                
                {/* Content */}
                <div className="relative p-6 z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-5xl">{stat.icon}</div>
                    <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-3 py-1">
                      <span className="text-xs font-semibold text-white">{stat.trend}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <h3 className="text-white text-opacity-90 text-sm font-medium">{stat.name}</h3>
                    <p className="text-5xl font-bold text-white mb-2">
                      {stat.value.toLocaleString()}
                    </p>
                    <p className="text-white text-opacity-75 text-xs">{stat.description}</p>
                  </div>
                </div>

                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 group-hover:translate-x-full transition-all duration-1000"></div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg p-2 mr-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Quick Actions</h3>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <button
                onClick={() => window.location.href = '/rss-ingestion'}
                className="group relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative flex flex-col items-center text-center space-y-2">
                  <div className="text-3xl mb-2">📡</div>
                  <span className="font-semibold">RSS Ingestion</span>
                  <span className="text-xs opacity-90">Fetch latest articles</span>
                </div>
              </button>

              <button
                onClick={() => window.location.href = '/epaper-upload'}
                className="group relative overflow-hidden bg-gradient-to-br from-green-500 to-emerald-700 hover:from-green-600 hover:to-emerald-800 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative flex flex-col items-center text-center space-y-2">
                  <div className="text-3xl mb-2">📄</div>
                  <span className="font-semibold">E-paper Upload</span>
                  <span className="text-xs opacity-90">Process newspapers</span>
                </div>
              </button>

              <button
                onClick={() => window.location.href = '/pending-questions'}
                className="group relative overflow-hidden bg-gradient-to-br from-amber-500 to-orange-700 hover:from-amber-600 hover:to-orange-800 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative flex flex-col items-center text-center space-y-2">
                  <div className="text-3xl mb-2">📝</div>
                  <span className="font-semibold">Review Questions</span>
                  <span className="text-xs opacity-90">{stats.pendingQuestions} pending</span>
                </div>
              </button>

              <button
                onClick={() => window.location.href = '/analytics'}
                className="group relative overflow-hidden bg-gradient-to-br from-purple-500 to-pink-700 hover:from-purple-600 hover:to-pink-800 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative flex flex-col items-center text-center space-y-2">
                  <div className="text-3xl mb-2">📊</div>
                  <span className="font-semibold">Analytics</span>
                  <span className="text-xs opacity-90">View insights</span>
                </div>
              </button>
            </div>
          </div>

          {/* System Status */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 rounded-lg p-2 mr-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">System Health</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Ingestion Service', status: 'running', color: 'green' },
                { name: 'Extractor Service', status: 'running', color: 'green' },
                { name: 'Judge Agent', status: 'running', color: 'green' },
                { name: 'Question Architect', status: 'running', color: 'green' },
                { name: 'Kafka Broker', status: 'running', color: 'green' },
                { name: 'MongoDB', status: 'running', color: 'green' },
                { name: 'Qdrant Vector DB', status: 'running', color: 'green' },
              ].map((service) => (
                <div
                  key={service.name}
                  className="group bg-gradient-to-br from-gray-50 to-gray-100 hover:from-green-50 hover:to-emerald-100 rounded-xl p-4 border border-gray-200 hover:border-green-300 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className={`h-3 w-3 bg-${service.color}-500 rounded-full animate-pulse`}></div>
                        <div className={`absolute inset-0 h-3 w-3 bg-${service.color}-500 rounded-full opacity-75`}></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                        {service.name}
                      </span>
                    </div>
                    <span className={`text-xs font-semibold text-${service.color}-600 bg-${service.color}-100 px-2 py-1 rounded-full`}>
                      {service.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
